import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Briefcase, CalendarRange, Clock3, LockKeyhole, MapPin } from 'lucide-react'
import { Button } from '../../components/ui/Button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { apiGet, apiPostForm, apiPostMultipart } from '../../lib/api.ts'

interface RoleOption {
  value: string
  label: string
}

interface EmploymentTypeOption {
  value: string
  label: string
  description: string
}

interface PracticeContext {
  practice_name: string
  postcode: string
  location: string
}

interface JobRecord {
  id: number
  requested_role: string
  employment_type: string
  job_title: string
  job_description: string
  request_date: string
  end_date: string
  start_time: string
  end_time: string
  location: string
  postcode: string
  pay_rate_type: string
  pay_rate_value: string
  notes: string
  job_status: string
  updated_at: string
  can_withdraw: boolean
}

const emptyForm = {
  requested_role: '',
  employment_type: '',
  request_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  pay_rate_type: 'hourly',
  pay_rate_value: '',
  job_description: '',
  notes: '',
}

export default function PostJobPage() {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const isDetailMode = Boolean(jobId)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([])
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState<EmploymentTypeOption[]>([])
  const [practice, setPractice] = useState<PracticeContext>({
    practice_name: '',
    postcode: '',
    location: '',
  })
  const [jobStatus, setJobStatus] = useState<'draft' | 'published' | 'filled' | 'closed' | 'cancelled' | ''>('')
  const [canWithdraw, setCanWithdraw] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [attachment, setAttachment] = useState<File | null>(null)

  useEffect(() => {
    void load()
  }, [])

  const load = async () => {
    try {
      const data = await apiGet<{
        request_role_options: RoleOption[]
        employment_type_options: EmploymentTypeOption[]
        practice: PracticeContext
        selected_job: JobRecord | null
      }>(`/practice/request-staff.php?format=json${isDetailMode ? `&job_id=${encodeURIComponent(jobId || '')}` : ''}`)

      setRoleOptions(data.request_role_options)
      setEmploymentTypeOptions(data.employment_type_options)
      setPractice(data.practice)
      if (data.selected_job) {
        setForm({
          requested_role: data.selected_job.requested_role || '',
          employment_type: data.selected_job.employment_type || '',
          request_date: data.selected_job.request_date || '',
          end_date: data.selected_job.end_date && data.selected_job.end_date !== data.selected_job.request_date ? data.selected_job.end_date : '',
          start_time: data.selected_job.start_time || '',
          end_time: data.selected_job.end_time || '',
          pay_rate_type: data.selected_job.pay_rate_type || 'hourly',
          pay_rate_value: data.selected_job.pay_rate_value || '',
          job_description: data.selected_job.job_description || '',
          notes: data.selected_job.notes || '',
        })
        setJobStatus((data.selected_job.job_status as typeof jobStatus) || '')
        setCanWithdraw(Boolean(data.selected_job.can_withdraw))
      } else {
        setJobStatus('')
        setCanWithdraw(false)
        setForm(emptyForm)
      }
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to load ${isDetailMode ? 'job details' : 'the job form'}.`)
    } finally {
      setLoading(false)
    }
  }

  const updateField = (field: keyof typeof emptyForm, value: string) => {
    setForm((current) => {
      const next = { ...current, [field]: value }

      if (field === 'employment_type') {
        if (value === 'locum') {
          next.end_date = ''
          next.pay_rate_type = next.pay_rate_type === 'daily' ? 'daily' : 'hourly'
        }

        if (value === 'temporary') {
          next.pay_rate_type = 'daily'
        }

        if (value === 'permanent') {
          next.end_date = ''
          next.start_time = ''
          next.end_time = ''
          next.pay_rate_value = ''
          next.pay_rate_type = 'hourly'
          next.notes = ''
        }
      }

      return next
    })
  }

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAttachment(event.target.files?.[0] ?? null)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setNotice('')

    const payload = new FormData()
    payload.append('action', isDetailMode ? 'update_job' : 'request_staff')
    if (isDetailMode && jobId) {
      payload.append('job_id', jobId)
    }
    payload.append('requested_role', form.requested_role)
    payload.append('employment_type', form.employment_type)
    payload.append('request_date', form.request_date)
    payload.append('end_date', form.end_date)
    payload.append('start_time', form.start_time)
    payload.append('end_time', form.end_time)
    payload.append('pay_rate_type', form.pay_rate_type)
    payload.append('pay_rate_value', form.pay_rate_value)
    payload.append('job_description', form.job_description)
    payload.append('notes', form.notes)
    payload.append('location', practice.location)
    payload.append('postcode', practice.postcode)

    if (attachment) {
      payload.append('job_description_file', attachment)
    }

    try {
      const data = await apiPostMultipart<{ message: string }>(
        '/practice/request-staff.php?format=json',
        payload
      )
      if (isDetailMode) {
        setNotice(data.message)
        await load()
      } else {
        navigate('/practice/jobs', { state: { notice: data.message } })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit job request.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleWithdraw = async () => {
    if (!jobId) {
      return
    }

    setSubmitting(true)
    setError('')
    setNotice('')

    try {
      const data = await apiPostForm<{ message: string }>('/practice/request-staff.php?format=json', {
        action: 'withdraw_job',
        job_id: jobId,
      })
      setNotice(data.message)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to withdraw job posting.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  const isLocum = form.employment_type === 'locum'
  const isTemporary = form.employment_type === 'temporary'
  const isPermanent = form.employment_type === 'permanent'
  const isEditable = !isDetailMode || jobStatus === 'draft'
  const pageTitle = isDetailMode ? 'Job Details' : 'Post New Job'
  const pageDescription = isDetailMode
    ? (isEditable ? 'This job is still in draft, so you can update the details below.' : 'This job has already been published or progressed, so the fields are locked.')
    : 'Create a practice job request without changing the live backend workflow.'
  const formDescription = isDetailMode && !isEditable
    ? ''
    : 'Employment type changes the fields below so the request matches the type of hire you are posting.'

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text-900">{pageTitle}</h1>
          <p className="mt-1 text-sm text-text-600">{pageDescription}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/practice/jobs')}>
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{notice}</div>}
      {isDetailMode && !isEditable && (
        <div className="flex items-center gap-2 rounded-lg bg-slate-100 p-3 text-sm text-slate-700">
          <LockKeyhole size={16} />
          This job is no longer in draft, so its details are read-only.
        </div>
      )}

      <Card className="overflow-hidden border-brand-200 shadow-md">
        <CardHeader className="bg-gradient-to-r from-brand-50 via-white to-brand-100">
          <CardTitle className="text-brand-800">{isDetailMode ? 'Job posting details' : 'New staffing request'}</CardTitle>
          {formDescription && <CardDescription>{formDescription}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="grid gap-3 md:grid-cols-3">
            <ContextField icon={Briefcase} label="Practice name" value={practice.practice_name || 'No practice name saved yet'} />
            <ContextField icon={MapPin} label="Postcode" value={practice.postcode || 'No postcode saved yet'} />
            <ContextField icon={MapPin} label="Location" value={practice.location || 'No location saved yet'} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-text-900">Role</label>
                <select
                  className="w-full rounded-xl border border-surface-600 px-3 py-3"
                  value={form.requested_role}
                  onChange={(event) => updateField('requested_role', event.target.value)}
                  disabled={!isEditable}
                  required
                >
                  <option value="">Select role</option>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-text-900">Employment type</label>
                <select
                  className="w-full rounded-xl border border-surface-600 px-3 py-3"
                  value={form.employment_type}
                  onChange={(event) => updateField('employment_type', event.target.value)}
                  disabled={!isEditable}
                  required
                >
                  <option value="">Select employment type</option>
                  {employmentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {form.employment_type && (
                  <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
                    {employmentTypeOptions.find((option) => option.value === form.employment_type)?.description}
                  </p>
                )}
              </div>
            </div>

            {form.employment_type && (
              <div className="space-y-4 rounded-2xl border border-surface-500 bg-slate-50/70 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-text-800">
                  <CalendarRange size={16} />
                  Job details
                </div>

                <div className={`grid gap-4 ${isPermanent ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
                  <FormField label="Start Date" required>
                    <input
                          className="w-full rounded-xl border border-surface-600 px-3 py-3"
                          type="date"
                          value={form.request_date}
                          onChange={(event) => updateField('request_date', event.target.value)}
                          disabled={!isEditable}
                          required
                        />
                  </FormField>

                  {isTemporary && (
                    <FormField label="End Date" required>
                      <input
                          className="w-full rounded-xl border border-surface-600 px-3 py-3"
                          type="date"
                          value={form.end_date}
                          onChange={(event) => updateField('end_date', event.target.value)}
                          disabled={!isEditable}
                          required
                        />
                    </FormField>
                  )}
                </div>

                {(isLocum || isTemporary) && (
                  <>
                    <div className="flex items-center gap-2 text-sm font-semibold text-text-800">
                      <Clock3 size={16} />
                      Shift times
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField label="Start Time" required>
                        <input
                          className="w-full rounded-xl border border-surface-600 px-3 py-3"
                          type="time"
                          value={form.start_time}
                          onChange={(event) => updateField('start_time', event.target.value)}
                          disabled={!isEditable}
                          required
                        />
                      </FormField>
                      <FormField label="End Time" required>
                        <input
                          className="w-full rounded-xl border border-surface-600 px-3 py-3"
                          type="time"
                          value={form.end_time}
                          onChange={(event) => updateField('end_time', event.target.value)}
                          disabled={!isEditable}
                          required
                        />
                      </FormField>
                    </div>
                  </>
                )}

                {isLocum && (
                  <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
                    <FormField label="Pay Type" required>
                      <select
                        className="w-full rounded-xl border border-surface-600 px-3 py-3"
                        value={form.pay_rate_type}
                        onChange={(event) => updateField('pay_rate_type', event.target.value)}
                        disabled={!isEditable}
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                      </select>
                    </FormField>
                    <FormField label="Pay Rate" required>
                      <input
                        className="w-full rounded-xl border border-surface-600 px-3 py-3"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.pay_rate_value}
                        onChange={(event) => updateField('pay_rate_value', event.target.value)}
                        disabled={!isEditable}
                        required
                      />
                    </FormField>
                  </div>
                )}

                {isTemporary && (
                  <FormField label="Daily Rate" required>
                    <input
                      className="w-full rounded-xl border border-surface-600 px-3 py-3"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.pay_rate_value}
                      onChange={(event) => updateField('pay_rate_value', event.target.value)}
                      disabled={!isEditable}
                      required
                    />
                  </FormField>
                )}

                <FormField label="Job Description" required>
                  <textarea
                    className="w-full rounded-xl border border-surface-600 px-3 py-3"
                    rows={isPermanent ? 8 : 5}
                    value={form.job_description}
                    onChange={(event) => updateField('job_description', event.target.value)}
                    disabled={!isEditable}
                    required
                  />
                </FormField>

                {(isLocum || isTemporary) && (
                  <FormField label="Notes">
                    <textarea
                      className="w-full rounded-xl border border-surface-600 px-3 py-3"
                      rows={4}
                      value={form.notes}
                      onChange={(event) => updateField('notes', event.target.value)}
                      disabled={!isEditable}
                    />
                  </FormField>
                )}

                {isPermanent && (
                  <FormField label="Tailored job description (PDF or DOCX)">
                    <input
                      className="w-full rounded-xl border border-dashed border-surface-500 bg-white px-3 py-3"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleAttachmentChange}
                      disabled={!isEditable}
                    />
                  </FormField>
                )}
              </div>
            )}

            {isEditable && (
              <Button
                type="submit"
                className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                disabled={submitting}
              >
                {submitting ? (isDetailMode ? 'Saving draft...' : 'Posting job...') : (isDetailMode ? 'Save Draft Changes' : 'Post New Job')}
              </Button>
            )}

            {isDetailMode && canWithdraw && (
              <Button
                type="button"
                variant="destructive"
                className="w-full h-12 text-base font-semibold"
                disabled={submitting}
                onClick={() => void handleWithdraw()}
              >
                {submitting ? 'Withdrawing job...' : 'Withdraw Job Posting'}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function ContextField({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof MapPin
  label: string
  value: string
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
        <Icon size={14} />
        {label}
      </div>
      <div className="text-sm font-medium text-slate-700">{value}</div>
    </div>
  )
}

function FormField({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block space-y-2">
      <span className="text-sm font-semibold text-text-900">
        {label}
        {required ? ' *' : ''}
      </span>
      {children}
    </label>
  )
}
